
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Client } from "@/types/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEmployees } from "@/hooks/useEmployees";
import { useCreateClientAssignment, useDeleteClientAssignment } from "@/hooks/useClientAssignments";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ClientAssignmentsProps {
  client: Client;
}

export const ClientAssignments = ({ client }: ClientAssignmentsProps) => {
  const [newAssignment, setNewAssignment] = useState<{ employee_id: string; role: string }>({ employee_id: "", role: "" });
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { employees = [] } = useEmployees();
  const createAssignmentMutation = useCreateClientAssignment();
  const deleteAssignmentMutation = useDeleteClientAssignment();

  const handleAddAssignment = async () => {
    if (!newAssignment.employee_id || !newAssignment.role) {
      toast.error("الرجاء اختيار الموظف والدور");
      return;
    }
    try {
      await createAssignmentMutation.mutateAsync({
        client_id: client.id,
        ...newAssignment
      });
      toast.success("تم تخصيص الموظف بنجاح");
      setNewAssignment({ employee_id: "", role: "" });
    } catch (error) {
      toast.error("حدث خطأ أثناء تخصيص الموظف");
      console.error(error);
    }
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!confirm("هل أنت متأكد من إزالة هذا التخصيص؟")) return;
    setDeletingId(assignmentId);
    try {
      await deleteAssignmentMutation.mutateAsync(assignmentId);
      toast.success("تم إزالة تخصيص الموظف");
    } catch (error) {
      toast.error("حدث خطأ أثناء إزالة التخصيص");
      console.error(error);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-4 pt-4 border-t">
      <h3 className="text-lg font-medium">الموظفون المخصصون</h3>
      <div className="space-y-2">
        {client.client_assignments?.map(assignment => (
          <div key={assignment.id} className="flex items-center justify-between p-2 border rounded-md bg-slate-50">
            <div>
              <p className="font-semibold">{assignment.employees?.name}</p>
              <p className="text-sm text-gray-500">{assignment.role}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteAssignment(assignment.id)}
              disabled={deletingId === assignment.id}
            >
              {deletingId === assignment.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <X className="w-4 h-4 text-red-500" />
              )}
            </Button>
          </div>
        ))}
        {client.client_assignments?.length === 0 && (
          <p className="text-sm text-center text-gray-500 py-2">لم يتم تخصيص أي موظف بعد.</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Select value={newAssignment.employee_id} onValueChange={value => setNewAssignment(prev => ({ ...prev, employee_id: value }))}>
          <SelectTrigger><SelectValue placeholder="اختر موظف" /></SelectTrigger>
          <SelectContent>
            {employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={newAssignment.role} onValueChange={value => setNewAssignment(prev => ({ ...prev, role: value }))}>
          <SelectTrigger><SelectValue placeholder="اختر دور" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sales">مبيعات</SelectItem>
            <SelectItem value="customer_service">خدمة عملاء</SelectItem>
            <SelectItem value="driver">سائق</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" onClick={handleAddAssignment} disabled={createAssignmentMutation.isPending}>
          {createAssignmentMutation.isPending ? "جاري الإضافة..." : "إضافة"}
        </Button>
      </div>
    </div>
  );
};
